import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const SESSION_STORAGE_KEY = 'wizard_session_id';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;
  const newId = crypto.randomUUID();
  window.localStorage.setItem(SESSION_STORAGE_KEY, newId);
  return newId;
}

interface UseWizardSessionArgs {
  professionId: string;
  currentStep: number;
  maxReachedStep: number;
  totalSteps: number;
}

export function useWizardSession({
  professionId,
  currentStep,
  maxReachedStep,
  totalSteps,
}: UseWizardSessionArgs) {
  const sessionIdRef = useRef<string>('');
  const initializedRef = useRef(false);

  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
    if (!sessionIdRef.current || initializedRef.current) return;
    initializedRef.current = true;

    supabase
      .from('wizard_sessions')
      .upsert(
        {
          session_id: sessionIdRef.current,
          profession_id: professionId,
          current_step: currentStep,
          max_reached_step: maxReachedStep,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'session_id' }
      )
      .then(({ error }) => {
        if (error) console.warn('wizard_sessions init failed', error.message);
      });
  }, [professionId, currentStep, maxReachedStep]);

  useEffect(() => {
    if (!sessionIdRef.current || !initializedRef.current) return;
    const payload: Record<string, unknown> = {
      current_step: currentStep,
      max_reached_step: maxReachedStep,
      profession_id: professionId,
      updated_at: new Date().toISOString(),
    };
    if (currentStep === totalSteps - 1) {
      payload.completed_at = new Date().toISOString();
    }
    supabase
      .from('wizard_sessions')
      .update(payload)
      .eq('session_id', sessionIdRef.current)
      .then(({ error }) => {
        if (error) console.warn('wizard_sessions update failed', error.message);
      });
  }, [currentStep, maxReachedStep, professionId, totalSteps]);
}
